<template>
  <MainLayout section="guides">
    <LightBox :items="images" :index="imageIndex" />

    <b-row class="guide_content">
      <b-col>
        <b-row>
          <b-col>
            <h1><router-link to="/guides">Guides</router-link> / Wallet Watching</h1>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <p>In this guide we'll cover the basics of setting up a filter to be notified of any transaction originating from or being sent to a Distributed Ledger Wallet.
            </p>

            <center>
              <iframe :width="mq_lte_sm ? 300 : 560" :height="mq_lte_sm ? 170 : 315" src="https://www.youtube.com/embed/ob-yi5QjbJU" title="Block Trac - Watching Wallets" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            </center>
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <b-row>
              <b-col>
                <h2>Setup</h2>

                <p>
                  Start by navigating to <router-link to="/txs">Block Tr.ac</router-link>
                </p>

                <p>
                  Register to create a new account or login.
                </p>
              </b-col>
            </b-row>

            <b-row :style="mq_lte_sm ? {'flex-direction': 'column'} : null">
              <b-col>
                <img :src="require('@/assets/guides/walletwatch/register-or-login.png')" @click="set_image" />
              </b-col>

              <b-col>
                <img :src="require('@/assets/guides/walletwatch/register.png')" @click="set_image"/>
              </b-col>

              <b-col>
                <img :src="require('@/assets/guides/walletwatch/login.png')" @click="set_image" />
              </b-col>
            </b-row>

            <b-row class="guide_subsection">
              <b-col>
                <p>Select the blockchain you want to monitor in the upper left.</p>
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <img :src="require('@/assets/guides/walletwatch/blockchain2.gif')" @click="set_image" :class="mq_gt_sm ? 'half_width' : null" />
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <h2>Watching for payments sent *To* a wallet</h2>

            <p>Click <b>"Add New Filter"</b></p>

            <img :src="require('@/assets/guides/walletwatch/add-filter.png')" @click="set_image" :class="mq_gt_sm ? 'half_width' : null" />

            <p>Select the <b>"To Account"</b> category</p>

            <img :src="require('@/assets/guides/walletwatch/to-wallet-filter.png')" @click="set_image" class="mq_gt_sm ? 'quarter_width' : null" />
          </b-col>
        </b-row>

        <b-row class="guide_subsection">
          <b-col>
            <p>Enter the address of the wallet to monitor.</p>

            <p>Give the filter a name.</p>

            <p>Enter an email address which to receive alerts.</p>

            <img :src="require('@/assets/guides/walletwatch/wallet-address-and-email.png')" @click="set_image" class="mq_gt_sm ? 'quarter_width' : null" />
          </b-col>
        </b-row>

        <b-row class="guide_subsection">
          <b-col>
            <p>Click <b>"Create Filter"</b>. It now appears in the <i>My Filters</i> list on the left.</p>

            <img :src="require('@/assets/guides/walletwatch/filter-list2.gif')" @click="set_image" class="mq_gt_sm ? 'half_width' : null" />

            <h4>Now whenever a transaction is sent to the wallet you will get an email!</h4>
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <h2>Watching for payments sent *From* a wallet</h2>

            <p>Create <b>"Add New Filter"</b> (see above).</p>

            <p>Select the <b>"From Account"</b> category</p>

            <img :src="require('@/assets/guides/walletwatch/from-account-filter.png')" @click="set_image" class="mq_gt_sm ? 'quarter_width' : null" />

            <p>Similar to <i>To Wallet</i> above, enter the address of the wallet to watch and the email address which to receive alerts.</p>

            <p>Click <b>"Create Filter"</b> and you are now done.</p>

            <h4>Whenever a transaction is sent from the wallet, you will get an email.</h4>
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <h2>Watching for payments sent *To* or *From* a wallet</h2>

            <p>In addition to the above, Block Trac provides matchers to match activity bi-directionally, either originating or directed toward an account</p>

            <p>Simply select the <b>"To or From Account"</b> category when creating a filter, the rest is the same.</p>

            <img :src="require('@/assets/guides/walletwatch/to-from-wallet-filter.png')" @click="set_image" class="mq_gt_sm ? 'quarter_width' : null" />
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <h2>Other Filter Categories</h2>

            <p>Block Trac support many categories of common distributed ledger activity, be sure to see <b>Category</b> dropdown options for.</p>

            <ul>
              <li>Payment Values and Fees</li>
              <li>Issued Currencies and Tokens</li>
              <li>DEX offers</li>
              <li>Smart Contract Execution</li>
              <li>NFTs</li>
              <li>Much more</li>
            </ul>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <b-alert show>
              <img :src="require('@/assets/exclamation-blue.svg')" width="25" />
              <span><b>Pro Tip</b>: You are not limited to the pre-built categories, you can match any type of DLT activity with Block Trac's powerful expression system!</span>
            </b-alert>
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <h2>Next steps</h2>
            <p>From here you can setup alerts to monitor for specific types and patterns of activity.</p>
            <p>Return to <router-link to="/guides">Guides</router-link> for more information.</p>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </MainLayout>
</template>

<script>
import MainLayout from '../components/MainLayout'
import LightBox from '../components/LightBox'
import Blockchain from '../mixins/blockchain'

export default{
  mixins : [Blockchain],

  components: {
    MainLayout,
    LightBox
  },

  data (){
    return {
      imageIndex : null,
      images : []
    }
  },

  methods : {
    set_image (evnt){
      this.images = [evnt.target.src];
      this.imageIndex = 0;
    }
  },

  mounted (){
    this.images = [];
    this.imageIndex = null;
  }
}
</script>
